import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAllProblems, ProblemReportItem } from "../../api/reportsApi";
import { NavigationBadge, useNavigationBadgesStore } from "../..";
import { cn } from "../../lib/utils";
import { downloadCSV } from "../../utils/exportUtils";

const fmt = (iso: string | null | undefined) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pl-PL");
};

type StatusFilter = "all" | "open" | "closed";
type TypeFilter = "all" | "major" | "minor";

export function ProblemsReportPage() {
  const navigate = useNavigate();
  const setItems = useNavigationBadgesStore((state: any) => state.setItems);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  useEffect(() => {
    setItems([{ text: "Raport Issues", url: "" } as NavigationBadge]);
  }, [setItems]);

  const { data, isLoading } = useQuery<ProblemReportItem[]>(
    ["all-problems"],
    fetchAllProblems,
    { refetchOnWindowFocus: false }
  );

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((p) => {
      // ukryj otwarte issues gdy raport jest zaakceptowany
      if (!p.resolvedDate && p.reportState === 3) return false;
      if (statusFilter === "open" && p.resolvedDate) return false;
      if (statusFilter === "closed" && !p.resolvedDate) return false;
      if (typeFilter === "major" && p.problemType !== 0) return false;
      if (typeFilter === "minor" && p.problemType !== 1) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          p.studyName.toLowerCase().includes(q) ||
          p.resortName.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.author.toLowerCase().includes(q) ||
          p.reportName.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [data, statusFilter, typeFilter, search]);

  const labelCls =
    "block text-[11px] font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em]";

  const pillBase = "px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors border";
  const pillActive = "bg-[#F4A832] text-[#0F0F0F] border-[#F4A832]";
  const pillInactive = "bg-transparent text-[var(--sc-muted)] border-[var(--sc-border)] hover:border-[#F4A832]";

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-[18px] font-semibold text-[var(--sc-text)]">
          Raport Issues
        </h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const rows = [
                ["Typ", "Kategoria", "Badanie", "Ośrodek", "Raport", "CRA", "Data otwarcia", "Data zamknięcia", "Ostatnia edycja", "Status"],
                ...filtered.map((p) => [
                  p.problemType === 0 ? "Major" : "Minor",
                  p.category,
                  p.studyName,
                  p.resortName,
                  p.reportName,
                  p.author || "",
                  p.identificationDate ? new Date(p.identificationDate).toLocaleDateString("pl-PL") : "",
                  p.resolvedDate ? new Date(p.resolvedDate).toLocaleDateString("pl-PL") : "",
                  p.lastEdited ? new Date(p.lastEdited).toLocaleDateString("pl-PL") : "",
                  p.resolvedDate ? "Zamknięty" : "Otwarty",
                ]),
              ];
              downloadCSV(rows, `Issues_${new Date().toISOString().slice(0, 10)}.csv`);
            }}
            disabled={filtered.length === 0}
            className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium bg-[var(--sc-text)] text-[var(--sc-surface)] hover:opacity-80 disabled:opacity-40 transition-colors"
          >
            ↓ Eksportuj CSV
          </button>
        <input
          type="text"
          placeholder="Szukaj (badanie, ośrodek, kategoria, opis, CRA)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm bg-[var(--sc-surface)] text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[#F4A832] w-80"
        />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex gap-1">
          {(["all", "open", "closed"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cn(pillBase, statusFilter === s ? pillActive : pillInactive)}
            >
              {s === "all" ? "Wszystkie" : s === "open" ? "Otwarte" : "Zamknięte"}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {(["all", "major", "minor"] as TypeFilter[]).map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={cn(pillBase, typeFilter === t ? pillActive : pillInactive)}
            >
              {t === "all" ? "Wszystkie typy" : t === "major" ? "Major" : "Minor"}
            </button>
          ))}
        </div>
        {data && (
          <span className="ml-auto text-xs text-[var(--sc-muted)] self-center">
            {filtered.length} / {data.length} issues
          </span>
        )}
      </div>

      {isLoading && (
        <p className="text-sm text-[var(--sc-muted)]">Ładowanie…</p>
      )}

      {!isLoading && filtered.length === 0 && (
        <p className="text-sm text-[var(--sc-muted)]">Brak issues spełniających kryteria.</p>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="rounded-[12px] border border-[var(--sc-border)] bg-[var(--sc-surface)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--sc-border)] bg-[var(--sc-bg)]">
                <th className="text-left px-4 py-3"><span className={labelCls}>Typ</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>Kategoria</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>Badanie</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>Ośrodek</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>Raport</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>CRA</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>Data otwarcia</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>Data zamknięcia</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>Ostatnia edycja</span></th>
                <th className="text-left px-4 py-3"><span className={labelCls}>Status</span></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const isClosed = !!p.resolvedDate;
                return (
                  <tr
                    key={p.id}
                    className={cn(
                      "border-b border-[var(--sc-border)] last:border-0",
                      i % 2 === 0 ? "" : "bg-[var(--sc-bg)]/40"
                    )}
                  >
                    <td className="px-4 py-3">
                      {p.problemType === 0 ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FDECEA] text-[#C03030]">
                          Major
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FFF8ED] text-[#C47A0A]">
                          Minor
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-text)] max-w-[160px]">
                      <span className="line-clamp-2">{p.category}</span>
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-text)] whitespace-nowrap">
                      {p.studyName}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-text)] whitespace-nowrap">
                      {p.resortName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/reports/${p.reportId}`)}
                        className="text-[#99AECB] hover:text-[#F4A832] text-xs underline-offset-2 hover:underline transition-colors text-left"
                      >
                        {p.reportName}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-muted)] whitespace-nowrap">
                      {p.author || "—"}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-text)] whitespace-nowrap">
                      {fmt(p.identificationDate)}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-text)] whitespace-nowrap">
                      {fmt(p.resolvedDate)}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-muted)] whitespace-nowrap">
                      {fmt(p.lastEdited)}
                    </td>
                    <td className="px-4 py-3">
                      {isClosed ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#E6F9F0] text-[#2BAE72]">
                          Zamknięty
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#EBEBEA] text-[var(--sc-muted)]">
                          Otwarty
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
